/**
 * Map Filters Component
 *
 * Search input with Jawg Places autocomplete for map view.
 * Simplified design without panel wrapper - just search bar and location button.
 * Uses Tailwind CSS for styling (light DOM rendering).
 *
 * @element peach-map-filters
 *
 * @fires location-selected - Emitted when a location is selected from autocomplete
 * @fires use-current-location - Emitted when user clicks the current location button
 *
 * @example
 * ```html
 * <peach-map-filters></peach-map-filters>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import { mapViewStore } from "@/features/map/stores/map-view-store";
import { jawgPlaces } from "@/services/jawg-places";
import { urlState } from "@/services/url-state";
import { cn } from "@/utils/cn";
import { getCachedIpLocation, getLocationFromIp } from "@/services/ip-geolocation";
import { matchLocationsByName } from "@/features/locations/utils/location-name-search";
import { applyNameSearch } from "@/features/map/utils/apply-name-search";
import type { MapFilters } from "@/types/map";
import type { SearchSuggestion } from "@/types/search-suggestion";

// Import UI components
import "@/features/ui/button";
import "@/features/ui/icon";

@customElement("peach-map-filters")
export class MapFiltersComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Whether to show the current location button
   */
  @property({ type: Boolean, attribute: "show-location-button" })
  declare showLocationButton: boolean;

  /**
   * Whether currently getting user's location
   */
  @property({ type: Boolean, attribute: "is-getting-location" })
  declare isGettingLocation: boolean;

  /**
   * Current filter values from store
   */
  @state()
  declare private filters: MapFilters;

  /**
   * Autocomplete suggestions shown in the dropdown.
   * A mix of name-matched pickup locations and Jawg locality results.
   * Note: NOT @state() to avoid re-renders that would disrupt input focus
   */
  private suggestions: SearchSuggestion[];

  /**
   * Whether autocomplete is loading
   * Note: NOT @state() to avoid re-renders that would disrupt input focus
   */
  private isSearching: boolean;

  /**
   * Whether to show autocomplete dropdown
   * Note: NOT @state() to avoid re-renders that would disrupt input focus
   */
  private showSuggestions: boolean;

  /**
   * Index of currently selected suggestion (keyboard navigation)
   * Note: NOT @state() to avoid re-renders that would disrupt input focus
   */
  private selectedIndex: number;

  /**
   * Store unsubscribe function
   */
  private unsubscribe?: () => void;

  /**
   * Debounce timer for search input
   */
  private searchDebounceTimer?: number;

  /**
   * Debounce delay (300ms in production, 0ms in tests)
   */
  private readonly debounceDelay: number;

  /**
   * Track if we're updating filters to prevent circular updates
   */
  private isUpdatingFilters = false;

  constructor() {
    super();
    this.showLocationButton = true;
    // Use instant debounce in test environments for faster test execution
    const isTest = typeof navigator !== 'undefined' && navigator.webdriver === true;
    this.debounceDelay = isTest ? 0 : 300;
    this.isGettingLocation = false;
    this.filters = {
      date: null,
      locationType: null,
      searchQuery: "",
    };
    this.suggestions = [];
    this.isSearching = false;
    this.showSuggestions = false;
    // -1 means nothing is pre-selected: Enter runs a name search over the
    // typed text rather than silently activating the first suggestion.
    this.selectedIndex = -1;
  }

  /**
   * Subscribe to map store on connect
   */
  connectedCallback() {
    super.connectedCallback();

    // Subscribe ONLY to searchQuery changes from external sources (URL params, etc.)
    // Use subscribeWithSelector to avoid re-rendering on every filter change
    this.unsubscribe = locationStore.subscribe(
      (state) => state.filters.searchQuery,
      (searchQuery) => {
        // Skip if we're the ones updating the store (prevent circular updates)
        if (this.isUpdatingFilters) {
          return;
        }

        // IMPORTANT: Only update the input DOM directly, not this.filters state
        // Updating @state() properties triggers Lit re-render which disrupts input focus
        const input = this.querySelector(
          "#location-search",
        ) as HTMLInputElement;
        if (!input) return;

        // Update input value even if focused (for external updates like URL restoration)
        // The input will maintain focus through the value update
        if (input.value !== searchQuery) {
          input.value = searchQuery;
          // Update clear button visibility
          this.updateLoadingIndicator(false);
        }
      },
      {
        fireImmediately: false, // Don't fire on mount, only on changes
      },
    );

    // Initialize from current state (for initial load from localStorage)
    const state = locationStore.getState();
    this.filters = state.filters;

    // Add document click listener to close suggestions
    document.addEventListener("click", this.handleDocumentClick);
  }

  /**
   * Ensure input value is set after first render
   * This handles cases where the store loads searchQuery from localStorage
   * before the component's first render completes.
   */
  firstUpdated() {
    const input = this.querySelector("#location-search") as HTMLInputElement;
    if (input && this.filters.searchQuery) {
      input.value = this.filters.searchQuery;
      // Show clear button if there's initial text
      this.updateLoadingIndicator(false);
    }
  }

  /**
   * Cleanup on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Remove document click listener
    document.removeEventListener("click", this.handleDocumentClick);
  }

  /**
   * Handle search input (debounced with Jawg API autocomplete)
   */
  private handleSearchInput(e: Event) {
    // Skip if we're programmatically updating (prevents circular updates)
    if (this.isUpdatingFilters) {
      return;
    }

    const input = e.target as HTMLInputElement;
    const searchQuery = input.value.trim();

    // Update clear button visibility immediately (without waiting for debounce)
    this.updateLoadingIndicator(false);

    // Clear existing timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Clear suggestions if query is empty
    if (!searchQuery) {
      this.suggestions = [];
      this.showSuggestions = false;
      this.updateSuggestionsDropdown();

      // IMPORTANT: Set flag BEFORE calling store methods to prevent subscription feedback
      this.isUpdatingFilters = true;

      // Only update store if the value actually changed
      const currentSearchQuery = locationStore.getState().filters.searchQuery;
      if (currentSearchQuery !== "") {
        locationStore.getState().updateFilters({ searchQuery: "" });
      }

      // Don't clear searchPoint here - user is just editing, not resetting
      // searchPoint is only cleared via the X button (handleClearSearch)

      // Reset the flag AFTER all store updates complete
      this.isUpdatingFilters = false;
      return;
    }

    // DO NOT CLEAR MAP STATE DURING TYPING
    // The user is just editing the search query, not confirming a new search yet.
    // Map state (searchPoint, searchMatchIds, selectedLocation, URL) should only
    // be cleared when:
    // 1. User selects a new autocomplete result (handleSuggestionClick)
    // 2. User explicitly clears the search input (handleClearSearch)
    // 3. User commits a new search via Enter / search icon (handleNameSearch)
    //
    // Because searchMatchIds is left alone here, an active name search's matches
    // stay on the map while the user types the next query: after a "Nashville"
    // name search, typing "Memphis" keeps the Nashville matches until the new
    // search commits. Deliberate, same as searchPoint.
    //
    // Clearing state here caused a 2.5 second freeze because it triggered:
    // - getVisibleLocations() recalculation (expensive Haversine formula)
    // - updateMarkers() full map rebuild (expensive DOM operations)
    // - fitBounds() map repositioning

    // Show loading state immediately
    this.isSearching = true;

    // Debounce API call to avoid excessive requests
    this.searchDebounceTimer = window.setTimeout(async () => {
      // Show loading state
      this.isSearching = true;
      this.updateLoadingIndicator(true);

      // Only search if query is at least 3 characters
      if (searchQuery.length < 3) {
        // Query too short - just clear suggestions and wait for more input
        this.suggestions = [];
        this.showSuggestions = false;
        this.updateSuggestionsDropdown();
        this.isSearching = false;
        this.updateLoadingIndicator(false);
        return;
      }

      // Match pickup locations by name against data already loaded in the
      // browser. Computed up front so name matches still show if Jawg fails.
      const locationSuggestions = this.getLocationNameSuggestions(searchQuery);

      try {
        // Bias Jawg ranking toward the user's location (from IP geolocation),
        // so an ambiguous query like "columbia" surfaces the nearest match.
        const cachedLocation = getCachedIpLocation();
        const places = await jawgPlaces.autocomplete({
          text: searchQuery,
          ...(cachedLocation
            ? { focusLat: cachedLocation.lat, focusLng: cachedLocation.lng }
            : {}),
        });
        const placeSuggestions: SearchSuggestion[] = (places ?? []).map(
          (place) => ({ ...place, kind: "place" }),
        );
        // Name-matched locations first (most specific), then localities.
        this.suggestions = [...locationSuggestions, ...placeSuggestions];
        // Show dropdown even when empty, so "No locations found" appears.
        this.showSuggestions = true;
      } catch (error) {
        console.error("[MapFilters] Jawg autocomplete error:", error);
        // Jawg failed, but name matches don't depend on it - still show those.
        // Don't show an empty "No locations found" on a transient API error.
        this.suggestions = locationSuggestions;
        this.showSuggestions = locationSuggestions.length > 0;
      } finally {
        // Don't pre-select a suggestion. Enter on the typed text runs a name
        // search across all matches; arrow keys opt into a specific suggestion.
        this.selectedIndex = -1;
        this.updateSuggestionsDropdown();
        this.isSearching = false;
        this.updateLoadingIndicator(false);
      }
    }, this.debounceDelay);
  } // end handleSearchInput

  /**
   * Build location-name suggestions from data already loaded in the store.
   *
   * @remarks
   * Jawg only geocodes localities, so a search for a pickup location by name
   * (e.g. "The Thirsty Goat") returns nothing from it. The names and
   * coordinates are already in memory for the list, so we match them here and
   * merge them into the dropdown.
   */
  private getLocationNameSuggestions(query: string): SearchSuggestion[] {
    const allLocations = locationStore.getState().getAllLocations();
    return matchLocationsByName(allLocations, query).map((location) => ({
      kind: "location",
      label: location.name,
      lat: location.lat,
      lng: location.lng,
      locationId: location.id,
      address: location.address,
    }));
  }

  /**
   * Handle suggestion selection
   *
   * @remarks
   * This is where we clear any previous search state and set new search coordinates.
   * Map state is only modified when user CONFIRMS a new selection, not during typing.
   *
   * A name-matched pickup location is additionally selected so its detail panel
   * opens; a Jawg locality clears any prior selection (the same behavior as before).
   */
  private handleSuggestionClick(suggestion: SearchSuggestion) {
    // Set flag to prevent @input event from interfering
    this.isUpdatingFilters = true;

    // Hide suggestions first to prevent @input event from clearing them
    this.showSuggestions = false;
    this.suggestions = [];
    this.updateSuggestionsDropdown();

    // Update search input value
    const input = this.querySelector("#location-search") as HTMLInputElement;
    if (input) {
      input.value = suggestion.label;
    }

    // Clear URL params when starting a new search (only on confirm, not typing)
    urlState.clearParams(["loc", "slot"]);

    // Select the matched location (opens its detail) or clear any prior
    // selection for a locality search.
    if (suggestion.kind === "location") {
      locationStore.getState().selectLocation(suggestion.locationId);
    } else {
      locationStore.getState().selectLocation(null);
    }

    // Center map on selected coordinates. Zoom closer for a specific pickup
    // location than for a whole locality.
    mapViewStore.getState().setMapCenter({
      lat: suggestion.lat,
      lng: suggestion.lng,
      zoom: suggestion.kind === "location" ? 13 : 10,
    });

    // Set search point for proximity filtering. For a name-matched location
    // this puts it at distance 0, so it sorts to the top of the list.
    locationStore.getState().setSearchPoint({
      lat: suggestion.lat,
      lng: suggestion.lng,
      radiusMiles: 50, // Show locations within 50 miles
    });

    // Update search query in store (persists to localStorage)
    locationStore.getState().updateFilters({ searchQuery: suggestion.label });

    // Reset flag to allow future @input events
    this.isUpdatingFilters = false;

    // Emit event
    this.dispatchEvent(
      new CustomEvent("location-selected", {
        detail: { suggestion },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Run a name search over the typed text.
   *
   * @remarks
   * When the query matches pickup locations by name (e.g. "Lowe's" — which can
   * match many locations), show the whole set of matches sorted by proximity
   * and pan to the nearest one, keeping the current zoom. This is distinct from
   * selecting a single suggestion, which centers on one point. When nothing
   * matches by name, fall back to the top geocoded suggestion (a
   * locality/zip/address) — the existing single-point behavior, which is
   * correct for a place query.
   */
  private handleNameSearch() {
    const input = this.querySelector("#location-search") as HTMLInputElement;
    const query = input?.value.trim() ?? "";
    if (!query) {
      return;
    }

    // Capture the top suggestion before any clearing, for the no-match fallback.
    const topSuggestion = this.suggestions[0];

    // Prevent the @input subscription from clobbering this programmatic update.
    this.isUpdatingFilters = true;
    const applied = applyNameSearch(query);
    this.isUpdatingFilters = false;

    if (applied) {
      // Committed to a multi-result search: close the dropdown.
      this.showSuggestions = false;
      this.suggestions = [];
      this.selectedIndex = -1;
      this.updateSuggestionsDropdown();
    } else if (topSuggestion) {
      // Not a pickup-location name — treat it as a place and use the top
      // geocoded suggestion (locality/zip/address), the existing behavior.
      this.handleSuggestionClick(topSuggestion);
    }
  }

  /**
   * Handle click outside to close suggestions
   */
  private handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const searchContainer = this.querySelector(".search-container");

    if (searchContainer && !searchContainer.contains(target)) {
      this.showSuggestions = false;
      this.updateSuggestionsDropdown();
    }
  };

  /**
   * Handle keyboard navigation in autocomplete
   */
  private handleKeyDown(e: KeyboardEvent) {
    // Enter commits a search whether or not the dropdown is open. If the user
    // arrowed to a specific suggestion, select it; otherwise run a name search
    // over the typed text (handleNameSearch).
    if (e.key === "Enter") {
      e.preventDefault();
      const hasNavigatedToSuggestion =
        this.showSuggestions &&
        this.selectedIndex >= 0 &&
        !!this.suggestions[this.selectedIndex];
      if (hasNavigatedToSuggestion) {
        this.handleSuggestionClick(this.suggestions[this.selectedIndex]);
      } else {
        this.handleNameSearch();
      }
      return;
    }

    // Arrow navigation and Escape only apply while the dropdown is open.
    if (!this.showSuggestions || this.suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length;
        this.updateSelectedSuggestionHighlight();
        break;

      case "ArrowUp":
        e.preventDefault();
        // From -1 (nothing selected) or 0, wrap to the last suggestion.
        this.selectedIndex =
          this.selectedIndex <= 0
            ? this.suggestions.length - 1
            : this.selectedIndex - 1;
        this.updateSelectedSuggestionHighlight();
        break;

      case "Escape":
        e.preventDefault();
        this.showSuggestions = false;
        this.updateSuggestionsDropdown();
        break;
    }
  }

  /**
   * Clear search input and suggestions
   *
   * @remarks
   * Clears the search and falls back to IP geolocation for location filtering.
   * Uses cached IP location for instant response.
   */
  private handleClearSearch() {
    const input = this.querySelector("#location-search") as HTMLInputElement;
    if (input) {
      input.value = "";
      input.focus();
    }

    this.suggestions = [];
    this.showSuggestions = false;
    this.updateSuggestionsDropdown();
    this.isUpdatingFilters = true;
    locationStore.getState().updateFilters({ searchQuery: "" });
    this.isUpdatingFilters = false;

    // Clear selection and URL params
    locationStore.getState().selectLocation(null);
    urlState.clearParams(["search", "loc", "slot"]);

    // Use cached IP location for instant response (from initial page load)
    const cachedLocation = getCachedIpLocation();
    if (cachedLocation) {
      mapViewStore.getState().setMapCenter({
        lat: cachedLocation.lat,
        lng: cachedLocation.lng,
        zoom: 11,
      });
      locationStore.getState().setSearchPoint({
        lat: cachedLocation.lat,
        lng: cachedLocation.lng,
        radiusMiles: 50,
      });
      return;
    }

    // No cached IP: clear the search synchronously so the list stops showing
    // stale name-search matches while the IP fetch is in flight. setSearchPoint(null)
    // also clears searchMatchIds and recalculates. updateFilters({ searchQuery: "" })
    // above does not recalculate, so without this the prior matches linger until the
    // fetch resolves (or indefinitely if it never does).
    locationStore.getState().setSearchPoint(null);

    // Then fetch IP location and re-center if one is found. On failure the
    // already-cleared state stands.
    getLocationFromIp().then((ipLocation) => {
      if (ipLocation) {
        mapViewStore.getState().setMapCenter({
          lat: ipLocation.lat,
          lng: ipLocation.lng,
          zoom: 11,
        });
        locationStore.getState().setSearchPoint({
          lat: ipLocation.lat,
          lng: ipLocation.lng,
          radiusMiles: 50,
        });
      }
    });
  }

  /**
   * Update loading indicator and clear button visibility without triggering re-render
   * Directly manipulates DOM to avoid focus loss
   */
  private updateLoadingIndicator(isLoading: boolean) {
    requestAnimationFrame(() => {
      const input = this.querySelector("#location-search") as HTMLInputElement;
      const loadingSpinner = this.querySelector(
        ".search-loading-spinner",
      ) as HTMLElement;
      const clearButton = this.querySelector(
        ".search-clear-button",
      ) as HTMLElement;
      const hasText = input && input.value.length > 0;

      if (loadingSpinner) {
        loadingSpinner.classList.toggle("hidden", !isLoading);
      }

      if (clearButton) {
        // Show clear button only if there's text AND not loading
        clearButton.style.display = hasText && !isLoading ? "flex" : "none";
      }
    });
  }

  /**
   * Update selected suggestion highlight without triggering re-render
   * Directly manipulates DOM to avoid focus loss
   */
  private updateSelectedSuggestionHighlight() {
    requestAnimationFrame(() => {
      const dropdown = this.querySelector(
        ".suggestions-dropdown",
      ) as HTMLElement;
      if (!dropdown) return;

      const buttons = dropdown.querySelectorAll("button");
      buttons.forEach((button, index) => {
        if (index === this.selectedIndex) {
          button.classList.add("bg-surface-subtle");
          button.classList.remove("hover:bg-surface-subtle");
        } else {
          button.classList.remove("bg-surface-subtle");
          button.classList.add("hover:bg-surface-subtle");
        }
      });
    });
  }

  /**
   * Update suggestions dropdown without triggering re-render
   * Directly manipulates DOM to avoid focus loss
   */
  private updateSuggestionsDropdown() {
    requestAnimationFrame(() => {
      const dropdown = this.querySelector(
        ".suggestions-dropdown",
      ) as HTMLElement;
      const input = this.querySelector("#location-search") as HTMLInputElement;
      if (!dropdown) return;

      // Safety check - component might be disconnecting
      if (!this.suggestions) return;

      // Save current focus state
      const hadFocus = input && document.activeElement === input;

      // Clear existing content
      dropdown.innerHTML = "";

      if (this.showSuggestions && this.suggestions.length > 0) {
        // Show dropdown
        dropdown.classList.remove("hidden");

        // Render suggestions
        this.suggestions.forEach((suggestion, index) => {
          const button = document.createElement("button");
          button.className =
            "w-full text-left px-4 py-3 transition-colors border-b border-muted last:border-b-0 focus:outline-none";
          button.type = "button"; // Prevent form submission
          button.tabIndex = -1; // Remove from tab order to prevent focus stealing

          if (index === this.selectedIndex) {
            button.classList.add("bg-surface-subtle");
          } else {
            button.classList.add("hover:bg-surface-subtle");
          }

          // Secondary line: a location's address, or a locality's layer
          // (e.g. "locality"). Localities get `capitalize`; addresses don't.
          const secondaryText =
            suggestion.kind === "location"
              ? suggestion.address
              : suggestion.layer;
          const secondaryClass =
            suggestion.kind === "location"
              ? "text-xs text-subtle mt-0.5"
              : "text-xs text-subtle mt-0.5 capitalize";

          // Only the static icon/scaffold goes through innerHTML. The label and
          // secondary line are user-facing data (location names/addresses from
          // the data feed, Jawg labels) and are set via textContent to avoid
          // any HTML injection.
          button.innerHTML = `
            <div class="flex items-start">
              <svg class="w-5 h-5 text-subtle mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div class="suggestion-text flex-1 min-w-0"></div>
            </div>
          `;

          const textContainer = button.querySelector(
            ".suggestion-text",
          ) as HTMLElement;

          const labelEl = document.createElement("div");
          labelEl.className = "text-sm font-medium text-default";
          labelEl.textContent = suggestion.label;
          textContainer.appendChild(labelEl);

          if (secondaryText) {
            const secondaryEl = document.createElement("div");
            secondaryEl.className = secondaryClass;
            secondaryEl.textContent = secondaryText;
            textContainer.appendChild(secondaryEl);
          }

          // Use mousedown instead of click to prevent focus change
          button.addEventListener("mousedown", (e) => {
            e.preventDefault(); // Prevent default focus behavior
            this.handleSuggestionClick(suggestion);
          });
          dropdown.appendChild(button);
        });
      } else if (
        this.showSuggestions &&
        this.suggestions.length === 0 &&
        !this.isSearching
      ) {
        // Show "no results" message
        dropdown.classList.remove("hidden");
        dropdown.innerHTML =
          '<div class="px-4 py-3"><p class="text-sm text-subtle">No locations found</p></div>';
      } else {
        // Hide dropdown
        dropdown.classList.add("hidden");
      }

      // Restore focus if it was on the input
      if (hadFocus && input) {
        input.focus();
      }
    });
  }

  /**
   * Handle current location button click
   */
  private handleUseCurrentLocation() {
    this.dispatchEvent(
      new CustomEvent("use-current-location", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    return html`
      <div class="search-container relative w-full">
        <!-- Search Input with Google Maps Style -->
        <div class="relative w-full">
          <input
            type="text"
            id="location-search"
            @input=${this.handleSearchInput}
            @keydown=${this.handleKeyDown}
            placeholder="Search by city, zip, or address..."
            class="w-full h-12 px-3 pl-12 pr-20 border border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand shadow-sm"
            autocomplete="off"
          />

          <!-- Search Icon (left side) - click to run a name search -->
          <button
            type="button"
            @click=${this.handleNameSearch}
            class="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 text-muted hover:text-default focus:outline-none focus:ring-2 focus:ring-brand rounded"
            aria-label="Search"
            title="Search"
          >
            <peach-icon name="search" size="md" class="pointer-events-none"></peach-icon>
          </button>

          <!-- Right side controls -->
          <div class="absolute right-0 top-0 h-full flex items-center">
            <!-- Loading Spinner (hidden by default) -->
            <div class="search-loading-spinner hidden px-3 border-l border-strong h-6 flex items-center">
              <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand"></div>
            </div>

            <!-- Clear Button (shown when there's text, hidden when loading) -->
            <button
              @click=${this.handleClearSearch}
              class="search-clear-button px-2 h-6 flex items-center text-subtle hover:text-muted focus:outline-none"
              style="display: none;"
              aria-label="Clear search"
            >
              <peach-icon name="x-mark" size="default"></peach-icon>
            </button>

            <!-- Vertical Divider + Current Location Button (Google Maps style) -->
            ${
              this.showLocationButton
                ? html`
                  <div class="h-6 w-px bg-border mr-1"></div>
                  <button
                    class=${cn(
                      "px-3 h-full flex items-center transition-colors focus:outline-none group",
                      {
                        "opacity-50 cursor-not-allowed": this.isGettingLocation,
                      },
                    )}
                    ?disabled=${this.isGettingLocation}
                    @click=${this.handleUseCurrentLocation}
                    title="Use my current location"
                    aria-label="Use my current location"
                  >
                    ${
                      this.isGettingLocation
                        ? html`
                          <div
                            class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand"
                          ></div>
                        `
                        : html`<peach-icon
                          name="crosshairs"
                          size="md"
                          class="text-default group-hover:text-brand transition-colors"
                        ></peach-icon>`
                    }
                  </button>
                `
                : ""
            }
          </div>
        </div>

        <!-- Autocomplete Dropdown (managed via direct DOM manipulation) -->
        <div class="suggestions-dropdown hidden absolute z-50 left-0 right-0 top-full mt-1 bg-surface border border-default rounded-lg shadow-lg max-h-64 overflow-y-auto"></div>
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-map-filters": MapFiltersComponent;
  }
}
